<template>
    <div>
        <slot
            :items="results"
            :filters="filters"
            :processresults="processResults"
            :opendialog="openDialog"
            :event="event"
            :scrolltoday="scrollToDay"
            :sort="sort"
            :sort-by="sortBy"
        >
        </slot>
    </div>
</template>

<script>
import { defineComponent, onMounted, ref } from 'vue';
import { cloneDeep, flatten, uniqBy } from 'lodash';
import Fuse from 'fuse.js';
import { getEventPerformances, getPerformances } from '../services/inventory';
import { setState } from '../services/stateful';

export default defineComponent({
    name: 'WhatsOnCalendar',
    props: {
        eventId: {
            type: String,
            default: '',
        }
    },
    setup(props) {
        const results = ref([]);
        const filters = ref([]);
        const performances = ref([]);
        const fuse = ref(null);
        const can_reset = ref(false);
        const total_results = ref(0);
        const event = ref({});
        const sortBy = ref('date');

        const setupFilters = () => {
            let aggregatedFilters = [
                {
                    title: 'Venues',
                    field: 'venue_filters',
                    facets: []
                },
                {
                    title: 'Event Type',
                    field: 'event_type_filters',
                    facets: []
                },
                {
                    title: 'Time of Day',
                    field: 'time_of_day',
                    facets: []
                },
                {
                    title: 'Genres',
                    field: 'genre',
                    facets: []
                },
                {
                    title: 'Season',
                    field: 'season',
                    facets: []
                },
            ];

            aggregatedFilters = aggregatedFilters.map((filter) => {
                let facets = performances.value.map((item) => {
                    return cloneDeep(item.filters[filter.field]);
                });

                facets = flatten(facets);

                facets = uniqBy(facets, (facet) => {
                    return facet.title;
                })
                    .filter(a => !!a.title);

                filter.facets = facets.map((facet) => {
                    facet.is_checked = false;
                    facet.is_disabled = false;

                    return facet;
                });

                return filter;
            });

            filters.value = aggregatedFilters;
        };

        const processResults = () => {
            const andQuery = [];

            for (const filter of filters.value) {
                const orQuery = [];

                for (const facet of filter.facets) {
                    if (facet.is_checked) {
                        orQuery.push({
                            [`filters.${filter.field}.title`]: `="${facet.title}"`
                        });
                    }
                }

                if (orQuery.length) {
                    andQuery.push({
                        ['$or']: orQuery
                    });
                }
            }
            let performancesResults = performances.value;
            let canReset = false;

            if (andQuery.length) {
                performancesResults = fuse.value
                    .search({
                        $and: andQuery
                    })
                    .map((result) => {
                        return result.item;
                    });

                canReset = true;
            }

            results.value = performancesResults;
            can_reset.value = canReset;
            total_results.value = performancesResults.length;
        };

        const openDialog = (e) => {
            event.value = e;
        };

        const scrollToDay = (day) => {
            const dayElement = document.getElementById('day-' + day.date);

            if (!dayElement) {
                return;
            }

            setTimeout(() => {
                const intersectionObserver = new IntersectionObserver(
                    (entries) => {
                        // eslint-disable-line compat/compat
                        if (entries[0].isIntersecting) {
                            dayElement.classList.add('calendar-list-day--focused');
                            intersectionObserver.disconnect();
                            setTimeout(() => {
                                dayElement.classList.remove('calendar-list-day--focused');
                            }, 50);
                        }
                    }
                );

                intersectionObserver.observe(dayElement);

                dayElement.scrollIntoView({
                    behavior: 'smooth',
                    block: 'center'
                });
            }, 50);
        };

        const sort = (by = 'date') => {
            sortBy.value = by;
            setState('events.sort_by', by);
        };

        const filterPerformances = (performancesList) => {
            let filteredPerformances = performancesList;

            if (props.eventId) {
                // Defensive net — the server-side ?eventId= scope is the source of truth
                filteredPerformances = filteredPerformances.filter(perf => perf.event.id === props.eventId);
            }

            return filteredPerformances;
        };

        onMounted(() => {
            const performancesPromise = props.eventId
                ? getEventPerformances(props.eventId)
                : getPerformances();

            performancesPromise.then((performancesResponse) => {
                performances.value = filterPerformances(performancesResponse);

                setupFilters();

                fuse.value = new Fuse(performances.value, {
                    threshold: 0.25,
                    useExtendedSearch: true,
                    includeScore: false,
                    shouldSort: false,
                    keys: [
                        'filters.venue_filters.title',
                        'filters.event_type_filters.title',
                        'filters.time_of_day.title',
                        'filters.genre.title',
                        'filters.season.title',
                    ]
                });

                processResults();
            });
        });

        return {
            filters,
            results,
            processResults,
            performances,
            openDialog,
            event,
            scrollToDay,
            sort,
            sortBy
        };
    }
});
</script>
